import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useWallet } from './walletContext';
import { getContract, getCaseDetails } from './contractUtils';

// Create context
const ClaimContext = createContext();

export const ClaimProvider = ({ children }) => {
  const [myClaimCount, setMyClaimCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { account, isConnected } = useWallet();

  const fetchClaimCount = useCallback(async () => {
    if (!isConnected || !account) {
      setMyClaimCount(0);
      return;
    }

    setIsLoading(true);
    try {
      await getContract();
      
      // For demo purposes, we'll check the first 100 cases
      const MAX_CASE_ID = 100;
      
      // Create an array to hold all valid cases that belong to the user
      let count = 0;
      
      // Process cases in smaller batches to avoid overwhelming the provider
      const BATCH_SIZE = 10;
      const casesToCheck = Array.from({ length: MAX_CASE_ID }, (_, i) => i);
      
      for (let i = 0; i < casesToCheck.length; i += BATCH_SIZE) {
        const batch = casesToCheck.slice(i, i + BATCH_SIZE);
        
        // Create promises for the current batch
        const batchPromises = batch.map(async (caseId) => {
          try {
            const details = await getCaseDetails(caseId);
            // Check if this case belongs to the user
            const isPlaintiff = details.plaintiff.toLowerCase() === account.toLowerCase();
            const isDefendant = details.defendant.toLowerCase() === account.toLowerCase();
            const isOwner = details.owner.toLowerCase() === account.toLowerCase();
            
            return isPlaintiff || isDefendant || isOwner ? 1 : 0;
          } catch (err) {
            // Silently ignore expected errors for non-existent tokens
            return 0;
          }
        });
        
        // Process this batch
        const batchResults = await Promise.all(batchPromises);
        
        // Sum up the count from this batch
        count += batchResults.reduce((sum, val) => sum + val, 0);
      }
      
      setMyClaimCount(count);
    } catch (err) {
      console.error("Error fetching claim count:", err);
    } finally {
      setIsLoading(false);
    }
  }, [account, isConnected]);

  // Update claim count when wallet changes
  useEffect(() => {
    fetchClaimCount();
  }, [fetchClaimCount]);

  // Provide value to consumers
  const value = {
    myClaimCount,
    isLoading,
    refreshClaims: fetchClaimCount
  };

  return (
    <ClaimContext.Provider value={value}>
      {children}
    </ClaimContext.Provider>
  );
};

// Custom hook to use the claim context
export const useClaims = () => {
  const context = useContext(ClaimContext);
  if (!context) {
    throw new Error('useClaims must be used within a ClaimProvider');
  }
  return context;
};